import { Grid } from '@mui/material';
import { getIn } from 'formik';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useMemo } from 'react';

import { DollarIcon } from 'assets/v2/icons';
import InputField from 'components/v2/InputField';
import useFormFieldValidator from 'utils/hooks/useFormFieldValidator';

/**
 * TripParkingForm
 *
 * A reusable component for managing tolls-related fields in trip forms.
 *
 * Handles toll amount input with validation and formatting.
 *
 * @param {Object} props
 * @param {Object} props.formik - Formik instance
 * @param {string} props.fieldPrefix - Dot-notation path for tolls, e.g. 'ancillaryServices.tolls' or 'itineraryForm.legs[0].tolls'
 * @param {boolean} props.isLoading - Disables input field while loading
 * @param {Object} [props.schema] - Optional Yup schema for required field validation
 * @param {boolean} [props.isRequired=true] - Whether the field is required
 * @param {string} [props.parkingAmountTitle] - Label for the parking cost
 */
const TripParkingForm = ({
  formik,
  fieldPrefix,
  isLoading,
  schema,
  isRequired = true,
  parkingAmountTitle = 'Amount Paid',
}) => {
  const { values, setFieldValue, setFieldTouched } = formik;

  const requiredFields = useMemo(
    () => (isRequired ? [`${fieldPrefix}.parkingAmountPaidInCents`] : []),
    [fieldPrefix, isRequired],
  );

  const { fieldErrorMsg, fieldValidation, setUpdatedField } =
    useFormFieldValidator({
      formik,
      schema,
      requiredFields,
    });

  const setValue = useCallback(
    async (key, value) => {
      const fullKey = `${fieldPrefix}.${key}`;
      await setFieldValue(fullKey, value);
      setFieldTouched(fullKey);
      setUpdatedField(fullKey);
    },
    [fieldPrefix, setFieldValue, setFieldTouched, setUpdatedField],
  );

  const handleAmountChange = useCallback(
    ({ target: { value } }) => {
      if (value === '') {
        setValue('parkingAmountPaidInCents', '');
      } else {
        const roundedValue = Math.round(parseFloat(value) * 100);
        setValue('parkingAmountPaidInCents', roundedValue.toString());
      }
    },
    [setValue],
  );

  const formValueParkingAmount = useMemo(() => {
    const formValue = getIn(values, `${fieldPrefix}.parkingAmountPaidInCents`);
    if (formValue !== 0 && !formValue) return '';
    return formValue / 100;
  }, [values, fieldPrefix]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <InputField
          type="number"
          autoComplete="off"
          label={parkingAmountTitle}
          adornment={<DollarIcon />}
          disabled={isLoading}
          required={isRequired}
          value={formValueParkingAmount}
          onChange={handleAmountChange}
          valid={fieldValidation[`${fieldPrefix}.parkingAmountPaidInCents`]}
          error={fieldErrorMsg[`${fieldPrefix}.parkingAmountPaidInCents`]}
        />
      </Grid>
    </Grid>
  );
};

TripParkingForm.propTypes = {
  formik: PropTypes.object.isRequired,
  fieldPrefix: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  schema: PropTypes.object,
  isRequired: PropTypes.bool,
  parkingAmountTitle: PropTypes.string,
};

export default memo(TripParkingForm);
